<template>
  <HeaderBar />

  <section class="carvia-container flex flex-col justify-center">
    <div class="mb-4 grid grid-cols-2 gap-4 md:flex md:justify-between">
      <Input v-model="search" variant="ghost" :placeholder="t('search')" class="w-full max-w-sm" />
      <CVButton :to="{ name: 'couponCreate' }">{{ t('createCoupon') }}</CVButton>
    </div>

    <Tabs v-model="activeTab" :tabs="tabs" class="mb-4" />

    <div class="grid gap-4">
      <PaginatedList :load="load" :query-key="['coupons', searchQuery, activeTab]">
        <template #entry="data">
          <CouponListItem :coupon="data.entry" />
        </template>
      </PaginatedList>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, toValue, watchEffect } from 'vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { useI18n } from 'vue-i18n';
import Input from '@/components/Input.vue';
import CouponListItem from '../components/CouponListItem.vue';
import { useUiStore } from '@/stores/ui.store';
import { refDebounced } from '@vueuse/core';
import PaginatedList from '@/components/PaginatedList.vue';
import { couponsService } from '@/api/coupons.service';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import Tabs, { type TabItem } from '@/components/Tabs.vue';
import { CouponType } from '@carvia/ros-client-types';

enum Tab {
  ALL = 'ALL',
  PROMO = 'PROMO',
}

const { t } = useI18n();
const uiStore = useUiStore();
watchEffect(() => {
  uiStore.setHeaderTitle(t('coupons'));
});

const search = useRouteQueryRef('search', '', 'string');
const searchQuery = refDebounced(search, 500);
const activeTab = ref<Tab>(Tab.ALL);

const tabs = computed<TabItem[]>(() => [
  { title: t('all'), id: Tab.ALL },
  { title: t('promo'), id: Tab.PROMO },
]);

const load = (page: number) =>
  couponsService.getAllWithQuery({
    page,
    limit: 10,
    search: toValue(searchQuery),
    type: activeTab.value === Tab.PROMO ? CouponType.PROMO : undefined,
  });
</script>

<i18n lang="json">
{
  "en": {
    "coupons": "Coupons",
    "createCoupon": "+ New Coupon",
    "search": "Search",
    "noCouponsFound": "No coupons found",
    "loadMore": "Load More",
    "all": "All",
    "promo": "Promo coupons"
  },
  "de": {
    "coupons": "Gutscheine",
    "createCoupon": "+ Neuer Gutschein",
    "search": "Suche",
    "noCouponsFound": "Keine Gutscheine gefunden",
    "loadMore": "Mehr laden",
    "all": "Alle",
    "promo": "Aktionsgutscheine"
  },
  "es": {
    "coupons": "Cupones",
    "createCoupon": "+ Nuevo cupón",
    "search": "Buscar",
    "noCouponsFound": "No se encontraron cupones",
    "loadMore": "Cargar más",
    "all": "Todos",
    "promo": "Cupones promocionales"
  }
}
</i18n>
